import { useEffect, useState, type FormEvent } from 'react'
import { PenLine } from 'lucide-react'
import type { Message } from '@shared/types'
import { api, mensajeDeError } from '../../../lib/api'
import { fechaCorta } from '../../../lib/format'
import { Button, ErrorMessage, Field, Loading, TextArea, useToast } from '../../../ui'
import { BOTON_CONTORNO, Seccion, cx } from './comunes'

/* ===========================================================================
   Libro de mensajes.

   Si la boda modera, lo decimos al enviar: nadie se queda esperando ver su
   mensaje publicado al instante cuando en realidad falta que lo aprueben.
   =========================================================================== */

const VISIBLES_AL_INICIO = 8

export interface LibroProps {
  slug: string
  /** La pareja puede cerrar el libro; entonces solo se lee. */
  permiteEscribir: boolean
  modera: boolean
}

export default function LibroDeMensajes({ slug, permiteEscribir, modera }: LibroProps) {
  const toast = useToast()
  const [mensajes, setMensajes] = useState<Message[]>([])
  const [cargando, setCargando] = useState(true)
  const [errorLista, setErrorLista] = useState<string | null>(null)
  const [todos, setTodos] = useState(false)

  const [nombre, setNombre] = useState('')
  const [texto, setTexto] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [errorEnvio, setErrorEnvio] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)

  useEffect(() => {
    let vigente = true
    setCargando(true)
    setErrorLista(null)

    api
      .get<{ messages: Message[] }>(`/api/public/${encodeURIComponent(slug)}/mensajes`)
      .then(({ messages }) => {
        if (vigente) setMensajes(messages)
      })
      .catch((fallo: unknown) => {
        if (vigente) setErrorLista(mensajeDeError(fallo))
      })
      .finally(() => {
        if (vigente) setCargando(false)
      })

    return () => {
      vigente = false
    }
  }, [slug])

  async function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (enviando) return

    const autor = nombre.trim()
    const cuerpo = texto.trim()
    if (autor === '' || cuerpo === '') {
      setErrorEnvio('Escribe tu nombre y tu mensaje.')
      return
    }

    setEnviando(true)
    setErrorEnvio(null)
    setAviso(null)

    try {
      const { message } = await api.post<{ message: Message }>(
        `/api/public/${encodeURIComponent(slug)}/mensajes`,
        { authorName: autor, body: cuerpo },
      )

      if (message.moderationStatus === 'aprobado') {
        setMensajes((previos) => [message, ...previos])
        setAviso('Publicado. Tu mensaje ya aparece en el libro.')
        toast.ok('Publicado')
      } else {
        setAviso('Recibido. Aparecerá en el libro en cuanto los novios lo aprueben.')
        toast.ok('Mensaje enviado')
      }

      setNombre('')
      setTexto('')
    } catch (fallo: unknown) {
      setErrorEnvio(mensajeDeError(fallo))
    } finally {
      setEnviando(false)
    }
  }

  const mostrados = todos ? mensajes : mensajes.slice(0, VISIBLES_AL_INICIO)

  return (
    <Seccion id="mensajes" eyebrow="Libro de mensajes" titulo="Déjanos unas palabras" ancho="ancho">
      <div
        className={
          permiteEscribir
            ? 'lg:grid lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:gap-12'
            : ''
        }
      >
      {/* El formulario nace plegado, por lo mismo que el programa salió de la
          página: tres campos desplegados eran 285 px de la invitación que casi
          nadie iba a usar en la primera visita. `<details>` nativo —abre con
          teclado y sin JavaScript—; el resumen es el botón. */}
      {permiteEscribir && (
        <details className="group max-w-md">
          <summary className="inline-flex min-h-11 cursor-pointer list-none items-center gap-2 text-sm font-medium text-tinta underline decoration-palo underline-offset-4 marker:content-none hover:decoration-laton">
            <PenLine size={16} aria-hidden="true" className="text-laton" />
            Escribir unas palabras
          </summary>
        <form onSubmit={enviar} noValidate className="mt-4 max-w-md">
          <div className="flex flex-col gap-4">
            <Field
              label="Tu nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              maxLength={80}
              autoComplete="name"
              required
            />
            <TextArea
              label="Tu mensaje"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              maxLength={1000}
              rows={4}
              hint={
                modera
                  ? 'Lo leemos antes de publicarlo, así que puede tardar un poco en aparecer.'
                  : 'Se publica en el libro apenas lo envíes.'
              }
              required
            />
            {errorEnvio !== null && <ErrorMessage>{errorEnvio}</ErrorMessage>}
            <div>
              <Button type="submit" loading={enviando}>
                Firmar el libro
              </Button>
            </div>
          </div>
          <p aria-live="polite" className="mt-3 text-sm text-verdemar">
            {aviso ?? ''}
          </p>
        </form>
        </details>
      )}

      {/* Sin formulario no hay rejilla: la lista adopta la medida editorial
          para no correr a lo ancho de todo el contenedor. */}
      <div className={permiteEscribir ? 'mt-6 lg:mt-0' : 'max-w-[46rem]'}>
        {cargando && <Loading label="Cargando los mensajes…" />}
        {!cargando && errorLista !== null && <ErrorMessage>{errorLista}</ErrorMessage>}

        {/* Con el libro vacío y el formulario a la vista, «escribe la primera
            línea» repetía lo que el botón de arriba ya invita a hacer. Solo se
            dice cuando el invitado no puede escribir. */}
        {!cargando && errorLista === null && mensajes.length === 0 && !permiteEscribir && (
          <p className="text-palo-hondo">Todavía no hay mensajes publicados.</p>
        )}

        {mostrados.length > 0 && (
          <ul className="flex flex-col">
            {mostrados.map((mensaje, i) => (
              <li key={mensaje.id} className={i === 0 ? '' : 'border-t border-palo/70 pt-4 mt-4'}>
                <p className="whitespace-pre-line leading-relaxed text-tinta">{mensaje.body}</p>
                <p className="mt-2 text-sm text-palo-hondo">
                  <span className="font-medium text-tinta/80">{mensaje.authorName}</span>
                  <span aria-hidden="true"> · </span>
                  <span className="tabular font-mono text-xs">{fechaCorta(mensaje.createdAt)}</span>
                </p>
              </li>
            ))}
          </ul>
        )}

        {!todos && mensajes.length > VISIBLES_AL_INICIO && (
          <button type="button" onClick={() => setTodos(true)} className={cx(BOTON_CONTORNO, 'mt-8')}>
            Ver los {mensajes.length} mensajes
          </button>
        )}
      </div>
      </div>
    </Seccion>
  )
}
